import React, { useMemo } from 'react';
import moment from 'moment';
import { shallowEqual, useSelector } from 'umi';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';

export const PERF_OVERVIEW_API =
  '/api/v1/dashboard/perf_overview/getPerfOverview';
export { PERF_OVERVIEW_API as api };

const DEFAULT_DATA_KEY = 'perfOverview';
const DEFAULT_GAME_TIMEZONE = 0;
const PLATFORM_CODE: Record<string, number> = {
  android: 0,
  ios: 1,
  windows: 5,
};

const resolveFormatter = (value: any, fallback: (input: any) => any) => {
  if (typeof value === 'string' && value) {
    return stringToFunction(value);
  }
  if (typeof value === 'function') {
    return value;
  }
  return fallback;
};

const getTimeZoneOffset = (timeZone: any, requestParams: any): number => {
  if (typeof timeZone === 'number' && Number.isFinite(timeZone)) {
    return timeZone;
  }
  const fromParams = Number(requestParams?.timezoneOffset);
  if (Number.isFinite(fromParams)) {
    return fromParams;
  }
  const matched = String(timeZone ?? '').match(/([+-]?\d+)/);
  return matched ? Number(matched[1]) : DEFAULT_GAME_TIMEZONE;
};

const resolvePlatform = (platform: any, requestParams: any): number => {
  if (typeof requestParams?.platform === 'number') {
    return requestParams.platform;
  }
  if (typeof platform === 'number') {
    return platform;
  }
  return PLATFORM_CODE[String(platform || '').toLowerCase()] ?? 0;
};

const resolveVersion = (requestParams: any): string => {
  if (requestParams?.version) {
    return String(requestParams.version);
  }
  const appVersion = requestParams?.app_version;
  if (Array.isArray(appVersion) && appVersion[0]) {
    return String(appVersion[0]);
  }
  return '';
};

const getPayload = (source: any) =>
  source?.data?.ret || source?.data || source?.ret || source || {};

export const formatBeforeRequest = (data: any = {}) => {
  const gameCode = data.gameCode;
  const platform = data.platform;
  const requestParams = data.requestParams || {};
  const tz = getTimeZoneOffset(data.timeZone, requestParams);
  const days = Number(requestParams?.dateRangeDays);
  const dateRangeDays = Number.isFinite(days) && days > 0 ? days : 14;
  const endTime = moment().utcOffset(tz, false).startOf('hour');
  const startTime = endTime.clone().subtract(dateRangeDays, 'days');
  const formatDateTime = (value: moment.Moment) =>
    value.format('YYYY-MM-DD HH:mm:ss');

  return {
    ...requestParams,
    game_code: gameCode,
    platform: resolvePlatform(platform, requestParams),
    version: resolveVersion(requestParams),
    end_time: formatDateTime(endTime),
    start_time: formatDateTime(startTime),
  };
};

export const formatData = (response: any) => {
  const payload = getPayload(response);
  return {
    last24h: payload?.last24h || {},
    lastWeek: payload?.lastWeek || {},
    msg: payload?.msg,
    requestid: payload?.requestid,
    endTime: response?.req?.end_time || payload?.end_time || '',
  };
};

export const getPerfOverviewState = (
  propsData: any,
  dataKey = DEFAULT_DATA_KEY,
) => {
  const serviceState = propsData?.[dataKey] || {};
  const error = serviceState?.errorType || serviceState?.errorMsg || null;
  return {
    data: serviceState?.data || {},
    loading: !!serviceState?.loading,
    error,
    isError: !!error,
  };
};

const PerformanceOverviewService = (props: any) => {
  const {
    style,
    className,
    api = PERF_OVERVIEW_API,
    dataKey = DEFAULT_DATA_KEY,
    data: propsData,
    children,
    platform,
    requestParams = {},
    formatBeforeRequest: formatBeforeRequestProp,
    formatData: formatDataProp,
    ...resetProps
  } = props || {};
  const gameCode = useSelector(
    (state: any) => state?.dashboard?.currentGame?.game_code,
    shallowEqual,
  );
  const data = useMemo(
    () => ({
      gameCode,
      platform,
      requestParams,
      ...(propsData || {}),
    }),
    [gameCode, platform, requestParams, propsData],
  );
  const beforeRequestHandler = resolveFormatter(
    formatBeforeRequestProp,
    formatBeforeRequest,
  );
  const formatDataHandler = resolveFormatter(formatDataProp, formatData);

  return (
    <div className={className} style={style}>
      <Service
        api={(params: any) => {
          if (!params?.game_code || params?.version === '') {
            return Promise.resolve({ data: {} });
          }
          if (typeof api === 'function') {
            return api(params);
          }
          return dashboardRequest({
            url: api || PERF_OVERVIEW_API,
            method: 'post',
            data: params,
            ignoreRequestId: false,
          });
        }}
        data={data}
        dataKey={dataKey}
        filterLoading={false}
        trackData={['gameCode', 'platform', 'requestParams']}
        beforeRequest={(reqData: any) =>
          beforeRequestHandler({
            ...reqData,
            gameCode: reqData?.gameCode || gameCode,
            platform: reqData?.platform ?? platform,
            requestParams: reqData?.requestParams || requestParams,
          })
        }
        formatData={formatDataHandler}
      >
        {React.Children.map(children, (child: any) =>
          React.cloneElement(child, {
            ...resetProps,
            dataKey,
          }),
        )}
      </Service>
    </div>
  );
};

export default PerformanceOverviewService;
